/**
 * Temporal smoothing — the mechanism that decouples render smoothness from the fixed tick.
 *
 * Port of the original's `TemporalSmoother` (`utils.hpp:81-100`, `utils.cpp:605-660`).
 *
 * Each smoothed quantity keeps a short history of timestamped samples. The renderer asks
 * for the value as it was RENDER_LAG_MS ago and gets an interpolation between the two
 * samples bracketing that instant. Because the requested time is in the past, both
 * bracketing samples already exist — so this interpolates rather than extrapolates, and
 * never overshoots.
 *
 * Positions blend linearly; rotations blend by normalised lerp, matching the original
 * (`utils.cpp` specialises MixData for Quaternion). Note that is nlerp, not slerp — the
 * original does not slerp, and the difference is visible on fast turns.
 */

import { Quaternion, Vector3 } from 'three';
import { RENDER_LAG_MS, SMOOTHER_SAMPLES } from './constants';

interface Sample<T> {
  data: T;
  timeMs: number;
}

abstract class TemporalSmootherBase<T> {
  private readonly samples: Sample<T>[] = [];
  private readonly capacity: number;

  constructor(capacity = SMOOTHER_SAMPLES) {
    this.capacity = capacity;
  }

  /** Record a simulation sample. Must be called with monotonically increasing times. */
  set(data: T, timeMs: number): void {
    this.samples.push({ data: this.clone(data), timeMs });
    if (this.samples.length > this.capacity) this.samples.shift();
  }

  /** Interpolated value as of `currentTimeMs - historyMs`. */
  get(currentTimeMs: number, historyMs = RENDER_LAG_MS): T {
    if (this.samples.length === 0) throw new Error('TemporalSmoother read before first set()');
    if (this.samples.length === 1) return this.clone(this.samples[0]!.data);

    const target = currentTimeMs - historyMs;

    // Before the oldest / after the newest: clamp rather than extrapolate.
    const oldest = this.samples[0]!;
    const newest = this.samples[this.samples.length - 1]!;
    if (target <= oldest.timeMs) return this.clone(oldest.data);
    if (target >= newest.timeMs) return this.clone(newest.data);

    for (let i = 0; i < this.samples.length - 1; i++) {
      const a = this.samples[i]!;
      const b = this.samples[i + 1]!;
      if (target >= a.timeMs && target <= b.timeMs) {
        const span = b.timeMs - a.timeMs;
        const bias = span === 0 ? 0 : (target - a.timeMs) / span;
        return this.mix(a.data, b.data, bias);
      }
    }
    return this.clone(newest.data);
  }

  clear(): void {
    this.samples.length = 0;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  protected abstract mix(a: T, b: T, bias: number): T;
  protected abstract clone(v: T): T;
}

export class Vector3Smoother extends TemporalSmootherBase<Vector3> {
  protected mix(a: Vector3, b: Vector3, bias: number): Vector3 {
    return a.clone().lerp(b, bias);
  }
  protected clone(v: Vector3): Vector3 {
    return v.clone();
  }
}

export class QuaternionSmoother extends TemporalSmootherBase<Quaternion> {
  /**
   * Normalised lerp, matching the original — NOT slerp. Keep it that way: slerp would be
   * "more correct" and would change how fast turns look.
   */
  protected mix(a: Quaternion, b: Quaternion, bias: number): Quaternion {
    // Shortest arc: flip b if the pair is more than 90 degrees apart.
    const dot = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
    const s = dot < 0 ? -1 : 1;
    const out = new Quaternion(
      a.x + (b.x * s - a.x) * bias,
      a.y + (b.y * s - a.y) * bias,
      a.z + (b.z * s - a.z) * bias,
      a.w + (b.w * s - a.w) * bias,
    );
    return out.normalize();
  }
  protected clone(v: Quaternion): Quaternion {
    return v.clone();
  }
}

export class NumberSmoother extends TemporalSmootherBase<number> {
  protected mix(a: number, b: number, bias: number): number {
    return a + (b - a) * bias;
  }
  protected clone(v: number): number {
    return v;
  }
}
